import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography,
  Chip,
  Autocomplete,
  CircularProgress
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
// eslint-disable-next-line
import styles from './NewMedicationModal.module.scss'

const DAYS_OF_WEEK = [
  { value: 'sun', label: 'Sunday' },
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' }
]

const PERIOD_UNITS = [
  { value: 'd', label: 'Daily' },
  { value: 'wk', label: 'Weekly' },
  { value: 'mo', label: 'Monthly' }
]

const NewMedicationModal = ({
  isOpen,
  setIsOpen,
  handleSubmit,
  patientId,
  me
}) => {
  const [medication, setMedication] = useState({
    name: '',
    id: '',
    dosage: '',
    unit: 'mg',
    doseQuantity: '',
    frequency: '1',
    periodUnit: 'd',
    selectedDays: [],
    timesOfDay: ['08:00'],
    instructions: ''
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [medicationOptions, setMedicationOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedMedication, setSelectedMedication] = useState(null)

  // Fetch medications from OpenFDA API
  useEffect(() => {
    const fetchMedications = async () => {
      if (!searchTerm || searchTerm.length < 3) {
        setMedicationOptions([])
        return
      }

      setLoading(true)
      try {
        // Using OpenFDA API
        const response = await fetch(
          `https://api.fda.gov/drug/label.json?search=openfda.brand_name:${searchTerm}&limit=10`
        )
        const data = await response.json()

        // Parse response
        const medications = []
        if (data.results && data.results.length > 0) {
          data.results.forEach((result) => {
            const brandNames = result.openfda?.brand_name || []
            const genericNames = result.openfda?.generic_name || []
            const substanceName = result.openfda?.substance_name || []
            const productId = result.openfda?.product_ndc?.[0] || ''

            if (brandNames.length > 0) {
              brandNames.forEach((name) => {
                medications.push({
                  label: name,
                  id: productId,
                  type: 'Brand',
                  details: {
                    generic: genericNames[0] || '',
                    substance: substanceName[0] || ''
                  }
                })
              })
            }

            // Add generic names as options too if they're not already added
            if (genericNames.length > 0) {
              genericNames.forEach((name) => {
                if (
                  !medications.some(
                    (med) => med.label.toLowerCase() === name.toLowerCase()
                  )
                ) {
                  medications.push({
                    label: name,
                    id: productId,
                    type: 'Generic',
                    details: {
                      substance: substanceName[0] || ''
                    }
                  })
                }
              })
            }
          })
        }

        setMedicationOptions(medications)
      } catch (error) {
        console.error('Error fetching medications:', error)
        // Try fallback search if first search fails
        try {
          const fallbackResponse = await fetch(
            `https://api.fda.gov/drug/label.json?search=openfda.generic_name:${searchTerm}&limit=10`
          )
          const fallbackData = await fallbackResponse.json()

          const fallbackMedications = []
          if (fallbackData.results && fallbackData.results.length > 0) {
            fallbackData.results.forEach((result) => {
              const brandNames = result.openfda?.brand_name || []
              const genericNames = result.openfda?.generic_name || []
              const substanceName = result.openfda?.substance_name || []
              const productId = result.openfda?.product_ndc?.[0] || ''

              if (genericNames.length > 0) {
                genericNames.forEach((name) => {
                  fallbackMedications.push({
                    label: name,
                    id: productId,
                    type: 'Generic',
                    details: {
                      brand: brandNames[0] || '',
                      substance: substanceName[0] || ''
                    }
                  })
                })
              }
            })
          }

          setMedicationOptions(fallbackMedications)
        } catch (fallbackError) {
          console.error('Fallback search also failed:', fallbackError)
          setMedicationOptions([])
        }
      } finally {
        setLoading(false)
      }
    }

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      fetchMedications()
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  const handleClose = () => {
    setIsOpen(false)
    setMedication({
      name: '',
      id: '',
      dosage: '',
      unit: 'mg',
      doseQuantity: '',
      frequency: '1',
      periodUnit: 'd',
      selectedDays: [],
      timesOfDay: ['08:00'],
      instructions: ''
    })
    setSearchTerm('')
    setMedicationOptions([])
    setSelectedMedication(null)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setMedication((prev) => ({
      ...prev,
      [name]: value
    }))
  }

  const handleMedicationSelect = (event, value) => {
    if (value) {
      setSelectedMedication(value)
      setMedication((prev) => ({
        ...prev,
        name: value.label,
        id: value.id
      }))
    } else {
      setSelectedMedication(null)
      setMedication((prev) => ({
        ...prev,
        name: '',
        id: ''
      }))
    }
  }

  const toggleDay = (day) => {
    setMedication((prev) => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter((d) => d !== day)
        : [...prev.selectedDays, day]
    }))
  }

  const addTime = () => {
    if (medication.timesOfDay.length < parseInt(medication.frequency, 10)) {
      setMedication((prev) => ({
        ...prev,
        timesOfDay: [...prev.timesOfDay, '20:00']
      }))
    }
  }

  const updateTime = (index, time) => {
    setMedication((prev) => ({
      ...prev,
      timesOfDay: prev.timesOfDay.map((t, i) => (i === index ? time : t))
    }))
  }

  const onSubmit = () => {
    if (
      medication.periodUnit === 'wk' &&
      medication.selectedDays.length === 0
    ) {
      alert('Please select at least one day for weekly medication')
      return
    }

    if (medication.timesOfDay.length !== parseInt(medication.frequency, 10)) {
      alert(
        `Please set ${medication.frequency} time${parseInt(medication.frequency, 10) > 1 ? 's' : ''} for medication`
      )
      return
    }

    const newMedicationRequest = {
      resourceType: 'MedicationRequest',
      status: 'active',
      intent: 'order',
      medicationCodeableConcept: {
        coding: [
          {
            system: 'http://www.nlm.nih.gov/research/umls/rxnorm',
            code: medication.id || '0',
            display: `${medication.name} ${medication.dosage}${medication.unit}`
          }
        ],
        text: `${medication.name} ${medication.dosage}${medication.unit}`
      },
      subject: {
        reference: `Patient/${patientId}`
      },
      authoredOn: new Date().toISOString(),
      requester: {
        reference: `Practitioner/${me?.id || 'unknown'}`
      },
      dosageInstruction: [
        {
          ...(medication.instructions && { text: medication.instructions }),
          timing: {
            repeat: {
              frequency: parseInt(medication.frequency, 10),
              period: 1,
              periodUnit: medication.periodUnit,
              ...(medication.periodUnit === 'wk' && {
                dayOfWeek: medication.selectedDays
              }),
              timeOfDay: medication.timesOfDay.map((time) => `${time}:00`)
            }
          },
          route: {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '26643006',
                display: 'Oral route'
              }
            ]
          },
          doseAndRate: [
            {
              doseQuantity: {
                value: parseInt(medication.doseQuantity, 10),
                unit: 'tablet'
              }
            }
          ]
        }
      ]
    }

    handleSubmit(newMedicationRequest)
    handleClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Medication</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Typography variant="subtitle1">1. Name</Typography>
          <Autocomplete
            value={selectedMedication}
            onChange={handleMedicationSelect}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue)
            }}
            options={medicationOptions}
            loading={loading}
            filterOptions={(x) => x} // We don't need MUI's filtering as we're getting filtered results from API
            noOptionsText={
              searchTerm.length < 3
                ? 'Type at least 3 characters'
                : 'No medications found'
            }
            getOptionLabel={(option) => option.label || ''}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body1">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.type}{' '}
                    {option.details?.generic
                      ? `• Generic: ${option.details.generic}`
                      : ''}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Medication Name"
                placeholder="e.g. Advil, Tylenol, etc."
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading
                        ? (
                        <CircularProgress color="inherit" size={20} />
                          )
                        : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />

          <Typography variant="subtitle1">2. Dosage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Dosage"
                name="dosage"
                value={medication.dosage}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                name="unit"
                value={medication.unit}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1">3. How Many Tablets?</Typography>
          <TextField
            label="Quantity"
            name="doseQuantity"
            value={medication.doseQuantity}
            onChange={handleChange}
            type="number"
            fullWidth
            required
          />

          <Typography variant="subtitle1">4. How Often?</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Frequency"
                name="frequency"
                value={medication.frequency}
                onChange={(e) => {
                  handleChange(e)
                  const newFrequency = parseInt(e.target.value, 10) || 1
                  setMedication((prev) => ({
                    ...prev,
                    timesOfDay: Array(newFrequency).fill('08:00')
                  }))
                }}
                type="number"
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Period</InputLabel>
                <Select
                  name="periodUnit"
                  value={medication.periodUnit}
                  onChange={handleChange}
                  label="Period"
                  required
                >
                  {PERIOD_UNITS.map((unit) => (
                    <MenuItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {medication.periodUnit === 'wk' && (
            <>
              <Typography variant="subtitle1">5. Select Days</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DAYS_OF_WEEK.map((day) => (
                  <Chip
                    key={day.value}
                    label={day.label}
                    onClick={() => toggleDay(day.value)}
                    color={
                      medication.selectedDays.includes(day.value)
                        ? 'primary'
                        : 'default'
                    }
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            </>
          )}

          <Typography variant="subtitle1">6. Set Times</Typography>
          {medication.timesOfDay.map((time, index) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <TextField
                type="time"
                value={time}
                onChange={(e) => updateTime(index, e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          ))}
          {medication.timesOfDay.length <
            parseInt(medication.frequency, 10) && (
            <Button
              startIcon={<AddIcon />}
              onClick={addTime}
              variant="outlined"
              sx={{ mt: 1 }}
            >
              Add Time
            </Button>
          )}

          <Typography variant="subtitle1">7. Instructions</Typography>
          <TextField
            label="Instructions"
            name="instructions"
            value={medication.instructions}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
            placeholder="e.g. Take with food"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={onSubmit} variant="contained" color="primary">
          Add Medication
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NewMedicationModal
